import { useState, useEffect, useRef } from 'react';
import { fetchCollection } from '../lib/cms';

export default function TestimonialSlider() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const timerRef = useRef(null);

  useEffect(() => {
    async function loadTestimonials() {
      const data = await fetchCollection('testimonials');
      setTestimonials(data);
      setLoading(false);
    }
    loadTestimonials();
  }, []);

  // Handle responsive column counts
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 640) {
        setVisibleCount(1); // Mobile
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2); // Tablet
      } else {
        setVisibleCount(3); // Desktop
      }
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clamp currentIndex when visibleCount or testimonials change
  useEffect(() => {
    if (testimonials.length > 0) {
      const maxIdx = Math.max(0, testimonials.length - visibleCount);
      if (currentIndex > maxIdx) {
        setCurrentIndex(maxIdx);
      }
    }
  }, [visibleCount, testimonials.length, currentIndex]);

  // Auto-play functionality
  const startTimer = () => {
    stopTimer();
    if (testimonials.length > visibleCount) {
      timerRef.current = setInterval(() => {
        handleNext();
      }, 5000);
    }
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  useEffect(() => {
    if (testimonials.length > 0) {
      startTimer();
    }
    return () => stopTimer();
  }, [testimonials, currentIndex, visibleCount]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#163A2B] border-t-transparent"></div>
      </div>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  const maxIndex = Math.max(0, testimonials.length - visibleCount);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  return (
    <section className="py-16 bg-white border-y border-[#E2E2E0]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-[#F4F1EA] px-3 py-1.5 rounded-full">
            Kundeanmeldelser
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1E1D] mt-4 font-display">
            Det siger vores kunder om os
          </h2>
          <p className="text-base text-[#1C1E1D]/70 mt-3">
            Gennem årene har vi hjulpet mange private boligejere, virksomheder og offentlige instanser i Skive og omegn med holdbare løsninger.
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative overflow-hidden px-2">
          {/* Track */}
          <div 
            className="flex transition-transform duration-500 ease-out"
            style={{
              transform: `translateX(-${testimonials.length > 0 ? currentIndex * (100 / testimonials.length) : 0}%)`,
              width: testimonials.length > visibleCount 
                ? `${(testimonials.length / visibleCount) * 100}%` 
                : '100%'
            }}
          >
            {testimonials.map((item) => (
              <div 
                key={item.id} 
                className="px-3 shrink-0"
                style={{ 
                  width: testimonials.length > visibleCount 
                    ? `${100 / testimonials.length}%` 
                    : `${100 / visibleCount}%` 
                }}
              >
                {/* Testimonial Card - NO hover effects as it is not clickable */}
                <div className="h-full bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg p-6 sm:p-8 flex flex-col justify-between shadow-sm">
                  <div>
                    {/* Stars */}
                    <div className="flex gap-1 text-amber-500 mb-4">
                      {[...Array(item.rating || 5)].map((_, i) => (
                        <i key={i} className="fa-solid fa-star text-sm" />
                      ))}
                    </div>
                    {/* Text */}
                    <div className="text-sm sm:text-base text-[#1C1E1D]/80 italic leading-relaxed [&_p]:inline [&_*]:!text-inherit">
                      "
                      {/<[a-z][\s\S]*>/i.test(item.text) ? (
                        <span dangerouslySetInnerHTML={{ __html: item.text }} />
                      ) : (
                        item.text
                      )}
                      "
                    </div>
                  </div>
                  
                  {/* Author Info */}
                  <div className="mt-6 pt-4 border-t border-[#E2E2E0] flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-sm sm:text-base text-[#1C1E1D]">{item.name}</h4>
                      {item.project && (
                        <p className="text-xs text-[#163A2B] font-semibold mt-0.5">{item.project}</p>
                      )}
                    </div>
                    <i className="fa-solid fa-quote-right text-3xl text-[#163A2B]/10" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Buttons - ONLY these are clickable, so they have hover effects */}
          {testimonials.length > visibleCount && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-[#E2E2E0] text-[#1C1E1D] hover:bg-[#163A2B] hover:text-white hover:border-[#163A2B] p-3 rounded-full shadow-md transition-all duration-200 focus:outline-none cursor-pointer"
                aria-label="Forrige anmeldelse"
              >
                <i className="fa-solid fa-chevron-left" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-[#E2E2E0] text-[#1C1E1D] hover:bg-[#163A2B] hover:text-white hover:border-[#163A2B] p-3 rounded-full shadow-md transition-all duration-200 focus:outline-none cursor-pointer"
                aria-label="Næste anmeldelse"
              >
                <i className="fa-solid fa-chevron-right" />
              </button>
            </>
          )}
        </div>

        {/* Indicators */}
        {testimonials.length > visibleCount && (
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(testimonials.length - visibleCount + 1)].map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  currentIndex === idx ? 'w-6 bg-[#163A2B]' : 'w-2 bg-[#E2E2E0] hover:bg-[#163A2B]/50'
                }`}
                aria-label={`Gå til slide ${idx + 1}`}
              />
            ))}
          </div>
        )}

      </div>
    </section>
  );
}
